import { HotmartRum } from '@hot-observability-js/react';
import { CHAT_NAMES, EVENTS } from 'common/constants';
import useAuthState from 'common/hooks/useAuthState';
import useChatMethods from 'common/hooks/useChatMethods';
import { AUTHZ, getChatEnablement } from 'common/services/authz-api';
import { dispatchCustomEvent } from 'common/utils/dispatchCustomEvent';
import { ChatEnablement } from 'models';
import { lazy, ReactElement, Suspense, useEffect } from 'react';
import { useQuery } from 'react-query';
import './style.scss';

// Chats
const Blip = lazy(() => import('./blip'));
const Zendesk = lazy(() => import('./zendesk'));
const FallbackWidget = lazy(() => import('common/components/FallbackWidget/FallbackWidget'));

export interface IChat {
  locale: string;
  guestAccess: boolean;
  userId: number;
  email?: string;
  name?: string;
  entityType?: string;
  isEnabled?: boolean;
  attendantId?: number;
  attendantEmail?: string;
}

function Chat(props: IChat): ReactElement {
  const { userId = '' } = props;
  const authState = useAuthState();
  const isAllowedRoute = !['welcome', 'communication'].some(path => location.pathname.split('/').includes(path));
  const shouldRenderChat = isAllowedRoute;

  const chatMethodsRef = useChatMethods();

  useEffect(() => {
    !!userId &&
      HotmartRum.setUser({
        id: userId,
      });
  }, [userId]);

  const { data, isSuccess, isLoading } = useQuery<ChatEnablement, Error>(
    [AUTHZ.GET_CHAT_ENABLEMENT, userId, authState],
    getChatEnablement,
    {
      enabled: shouldRenderChat,
      onError: (e: Error) => {
        HotmartRum.captureException(e);
      },
      onSuccess: (data: ChatEnablement) => {
        dispatchCustomEvent(EVENTS.ON_CHAT_INIT, data);

        if (data.chatName !== CHAT_NAMES.BLIP_ASYNC_BUYER) {
          const bubble = document.querySelector('.speech-bubble');
          bubble?.setAttribute('style', 'display: none');
        }
      },
    }
  );

  const { chatName = '', openChatBW, chat } = data || {};
  const canRender = isSuccess && Boolean(chat);

  const ChatOptions = {
    [CHAT_NAMES.BLIP_ASYNC_BUYER]: Blip,
    [CHAT_NAMES.BLIP_SYNC_CREATOR_FALLBACK]: Blip,
    [CHAT_NAMES.ZENDESK_SYNC_CREATOR]: Zendesk,
    [CHAT_NAMES.HOTMART_ASYNC_CREATOR_FALLBACK]: FallbackWidget,
    [CHAT_NAMES.ZENDESK_ASYNC_CREATOR]: Zendesk,
    [CHAT_NAMES.ZENDESK_CAIO]: Zendesk,
    [CHAT_NAMES.ZENDESK_CAIO_SUNSHINE]: Zendesk,
  };

  const ChatWidget = ChatOptions[chatName];

  if (!ChatWidget || !canRender || isLoading) {
    return <></>;
  }

  return (
    <Suspense fallback={<></>}>
      <ChatWidget {...props} openChatBW={openChatBW} chatName={chatName} ref={chatMethodsRef} />
    </Suspense>
  );
}

export default Chat;
